import { useState, useEffect, useRef } from "react";

interface Options {
  duration?: number;
}

/**
 * Animates a number from 0 to the target value over `duration` ms.
 * Returns the current animated value (integer).
 */
export function useAnimatedCounter(target: number, { duration = 1200 }: Options = {}): number {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);
  const prevTarget = useRef(0);

  useEffect(() => {
    const from = prevTarget.current;
    prevTarget.current = target;
    startTime.current = performance.now();

    let raf: number;
    const animate = (now: number) => {
      const elapsed = now - (startTime.current ?? now);
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}
